/**
 * AP-34 15., 8.3 — Bausteine der Maske für Subunternehmer: HFU-Prüfung am Zahltag, Rücklässe der
 * Rechnung, Abzug der Teil- und Anzahlungsrechnungen auf der Schlussrechnung.
 *
 * Eintragen, Anlegen, Freigeben und Abziehen sind Handlungen — jede hat ihren Knopf (`speichern.md`, Fall 3).
 */
import { useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { feldKlasse } from '@/lib/speichern'
import { leseZahl } from '@/lib/speichern/zahl'
import { addHfuCheck, addRetention, deductInvoices, deleteRetention, fetchDeductionCandidates, fetchHfuChecks, fetchRetentions, patchRetention, releaseCoverRetentions, releaseRetention } from '../api/incoming-invoices.api'
import { ART_TEXT, tagText } from '../lib/texte'
import type { DeductionCandidate, HfuCheck, IncomingInvoiceDetail, RetentionOverview } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const haupt = 'rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'
export const RUECKLASS_TEXT: Record<string, string> = { COVER_RETENTION: 'Deckungsrücklass', WARRANTY_RETENTION: 'Haftrücklass' }

type Props = { rechnung: IncomingInvoiceDetail; onFehler: (t: string | null) => void }

/** 15.2 — HFU-Prüfung: Maßgeblich ist der Zahltag. Der Benutzer fragt die Liste ab und trägt das Ergebnis ein. */
export function HfuPruefung({ rechnung, onFehler, onGeaendert }: Props & { onGeaendert: () => void }) {
  const { user } = useAuthStore()
  const darf = erfuelltRecht('incoming_invoices.payment.manage', user?.permissions)
  const [pruefungen, setPruefungen] = useState<HfuCheck[]>([])
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10))
  const [beleg, setBeleg] = useState<File | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const lieferant = rechnung.supplierId

  useEffect(() => {
    if (!lieferant) return
    let aus = false
    fetchHfuChecks(lieferant)
      .then((p) => !aus && setPruefungen(p))
      .catch(() => !aus && setPruefungen([]))
    return () => {
      aus = true
    }
  }, [lieferant])

  const eintragen = async (gelistet: boolean) => {
    if (!lieferant) return
    try {
      setLaeuft(true)
      onFehler(null)
      setPruefungen(await addHfuCheck(lieferant, datum, gelistet, beleg))
      setBeleg(null)
      onGeaendert()
    } catch (e) {
      onFehler(apiErrorText(e, 'Die HFU-Prüfung ließ sich nicht eintragen.'))
    } finally {
      setLaeuft(false)
    }
  }
  const letzte = pruefungen[0]

  return (
    <div className="rounded-md border border-border px-3 py-2 text-sm">
      <p className="font-medium">Auftraggeberhaftung</p>
      <p className="mt-0.5 text-muted-foreground">
        Bezahlt wird ohne Abzug nur, wenn der Subunternehmer <strong>am Zahltag</strong> auf der HFU-Gesamtliste steht. Sonst geht der Haftungsbetrag an das Dienstleistungszentrum. Frag die Liste bei der Sozialversicherung ab und trag das Ergebnis hier ein.
      </p>
      <p className="mt-1">
        {letzte ? (
          <>
            Letzte Prüfung vom {tagText(letzte.checkedOn)}: <span className={letzte.listed ? 'font-medium text-emerald-700 dark:text-emerald-300' : 'font-medium text-destructive'}>{letzte.listed ? 'gelistet' : 'nicht gelistet'}</span>
            {letzte.hasEvidence && <span className="text-muted-foreground"> · mit Beleg</span>}
          </>
        ) : (
          <span className="text-muted-foreground">Noch keine Prüfung eingetragen.</span>
        )}
      </p>
      {darf && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted-foreground">
            geprüft am
            <input type="date" aria-label="HFU geprüft am" className={`${feldKlasse} mt-0.5 w-40`} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            Beleg (Ausdruck)
            <input type="file" aria-label="Beleg der HFU-Prüfung" accept="application/pdf,image/png,image/jpeg" className="mt-0.5 block text-xs" onChange={(e) => setBeleg(e.target.files?.[0] ?? null)} />
          </label>
          <button type="button" className={knopf} disabled={laeuft || !datum} onClick={() => void eintragen(true)}>
            Gelistet eintragen
          </button>
          <button type="button" className={`${knopf} text-destructive`} disabled={laeuft || !datum} onClick={() => void eintragen(false)}>
            Nicht gelistet eintragen
          </button>
        </div>
      )}
    </div>
  )
}

/** 15.4 — Rücklässe der Rechnung: mindern den Zahlbetrag, nicht die Rechnung. */
export function Ruecklaesse({ rechnung, onFehler, onGeaendert }: Props & { onGeaendert: () => void }) {
  const { user } = useAuthStore()
  const darf = erfuelltRecht('incoming_invoices.payment.manage', user?.permissions)
  const [stand, setStand] = useState<RetentionOverview | null>(null)
  const [art, setArt] = useState<'COVER_RETENTION' | 'WARRANTY_RETENTION'>(rechnung.kind === 'FINAL_INVOICE' ? 'WARRANTY_RETENTION' : 'COVER_RETENTION')
  const [prozent, setProzent] = useState('')
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    let aus = false
    fetchRetentions(rechnung.id)
      .then((s) => !aus && setStand(s))
      .catch(() => !aus && setStand(null))
    return () => {
      aus = true
    }
  }, [rechnung.id, rechnung.updatedAt, rechnung.lines.length])

  // Vorbelegung des Satzes aus der Einstellung
  useEffect(() => {
    if (!stand) return
    const v = art === 'COVER_RETENTION' ? stand.defaults.coverPercent : stand.defaults.warrantyPercent
    setProzent(v ? String(v).replace('.', ',') : '')
  }, [art, stand?.defaults.coverPercent, stand?.defaults.warrantyPercent]) // eslint-disable-line react-hooks/exhaustive-deps

  const tue = async (was: () => Promise<RetentionOverview>, fehlertext: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      setStand(await was())
      onGeaendert()
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
    } finally {
      setLaeuft(false)
    }
  }
  const anlegen = () => {
    const p = leseZahl(prozent)
    if (p === null || Number.isNaN(p) || p <= 0) return onFehler('Der Satz des Rücklasses muss eine Zahl über null sein.')
    return tue(() => addRetention(rechnung.id, { kind: art, percent: p }), 'Der Rücklass ließ sich nicht anlegen.')
  }

  if (!stand) return null
  const offeneDeckung = stand.coverOfParts.filter((x) => !x.releasedAt)
  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted-foreground">Ein Rücklass mindert, was gezahlt wird — die Rechnung, ihre Kosten und die Übergabe an die Buchhaltung bleiben in voller Höhe.{stand.retained > 0 && <> Einbehalten: <span className="font-medium tabular-nums text-foreground">{geldText(stand.retained)}</span></>}</p>

      {stand.retentions.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {stand.retentions.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5">
              <span className="font-medium">{RUECKLASS_TEXT[x.kind] ?? x.kind}</span>
              <span className="tabular-nums text-muted-foreground">{String(x.percent).replace('.', ',')} % von {geldText(x.baseAmount)}</span>
              <span className="font-medium tabular-nums">{geldText(x.amount)}</span>
              {x.releaseDate && <span className="text-muted-foreground">fällig {tagText(x.releaseDate)}</span>}
              {x.releasedAt ? (
                <span className="rounded bg-emerald-500/15 px-1.5 text-xs text-emerald-700 dark:text-emerald-300">freigegeben {tagText(x.releasedAt)}</span>
              ) : x.replacedByGuarantee ? (
                <span className="rounded bg-sky-500/15 px-1.5 text-xs text-sky-700 dark:text-sky-300">Bankgarantie{x.guaranteeValidUntil ? ` bis ${tagText(x.guaranteeValidUntil)}` : ''}</span>
              ) : (
                <span className="rounded bg-amber-500/15 px-1.5 text-xs text-amber-700 dark:text-amber-300">einbehalten</span>
              )}
              {darf && !x.releasedAt && (
                <span className="ml-auto flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-muted-foreground">
                    <input type="checkbox" checked={x.replacedByGuarantee} disabled={laeuft} onChange={(e) => void tue(() => patchRetention(rechnung.id, x.id, { replacedByGuarantee: e.target.checked }), 'Der Rücklass ließ sich nicht ändern.')} /> durch Bankgarantie abgelöst
                  </label>
                  <button type="button" className={knopf} disabled={laeuft} title="Der Rücklass wird zahlbar und steht im nächsten Zahlungsvorschlag" onClick={() => void tue(() => releaseRetention(rechnung.id, x.id), 'Der Rücklass ließ sich nicht freigeben.')}>
                    Freigeben
                  </button>
                  <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" disabled={laeuft} aria-label={`${RUECKLASS_TEXT[x.kind]} löschen`} title="Rücklass löschen" onClick={() => void tue(() => deleteRetention(rechnung.id, x.id), 'Der Rücklass ließ sich nicht löschen.')}>
                    <Trash2 size={14} />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {stand.coverOfParts.length > 0 && (
        <div className="rounded-md border border-border px-3 py-2">
          <p className="font-medium">Deckungsrücklässe der abgezogenen Teilrechnungen</p>
          <ul className="mt-1 space-y-0.5">
            {stand.coverOfParts.map((x) => (
              <li key={x.id} className="flex flex-wrap gap-x-3 text-muted-foreground">
                <span>Rechnung {x.invoiceNumber}</span>
                <span className="tabular-nums text-foreground">{geldText(x.amount)}</span>
                <span>{x.releasedAt ? `freigegeben ${tagText(x.releasedAt)}` : 'einbehalten'}</span>
              </li>
            ))}
          </ul>
          {darf && offeneDeckung.length > 0 && (
            <button type="button" className={`${knopf} mt-2`} disabled={laeuft} onClick={() => void tue(() => releaseCoverRetentions(rechnung.id), 'Die Deckungsrücklässe ließen sich nicht freigeben.')}>
              Mit Schlussrechnung freigeben ({offeneDeckung.length})
            </button>
          )}
        </div>
      )}

      {darf && rechnung.status !== 'CANCELLED' && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted-foreground">
            Art
            <select aria-label="Art des Rücklasses" className={`${feldKlasse} mt-0.5 w-44`} value={art} onChange={(e) => setArt(e.target.value as typeof art)}>
              <option value="COVER_RETENTION">Deckungsrücklass</option>
              <option value="WARRANTY_RETENTION">Haftrücklass</option>
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            Satz %
            <input inputMode="decimal" aria-label="Satz des Rücklasses in Prozent" className={`${feldKlasse} mt-0.5 w-24 text-right tabular-nums`} value={prozent} onChange={(e) => setProzent(e.target.value)} />
          </label>
          <span className="pb-1.5 text-xs text-muted-foreground">von {geldText(stand.defaults.base)}{art === 'WARRANTY_RETENTION' ? ` · fällig ${stand.defaults.warrantyMonths} Monate nach dem Rechnungsdatum` : ''}</span>
          <button type="button" className={knopf} disabled={laeuft} onClick={() => void anlegen()}>
            Rücklass anlegen
          </button>
        </div>
      )}
    </div>
  )
}

/** 8.3 — Schlussrechnung: Teil- und Anzahlungsrechnungen als Abzugszeilen übernehmen. */
export function SchlussAbzug({ rechnung, onFehler, onRechnung }: Props & { onRechnung: (r: IncomingInvoiceDetail) => void }) {
  const [kandidaten, setKandidaten] = useState<DeductionCandidate[]>([])
  const [wahl, setWahl] = useState<Set<string>>(new Set())
  const [laeuft, setLaeuft] = useState(false)
  const abzuege = rechnung.lines.filter((z) => z.lineType === 'DEDUCTION').length

  useEffect(() => {
    let aus = false
    fetchDeductionCandidates(rechnung.id)
      .then((k) => {
        if (aus) return
        setKandidaten(k)
        setWahl(new Set(k.map((x) => x.id)))
      })
      .catch(() => !aus && setKandidaten([]))
    return () => {
      aus = true
    }
  }, [rechnung.id, rechnung.supplierId, rechnung.defaultProjectId, abzuege])

  if (!kandidaten.length) return null
  const abziehen = async () => {
    try {
      setLaeuft(true)
      onFehler(null)
      onRechnung(await deductInvoices(rechnung.id, [...wahl]))
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Rechnungen ließen sich nicht abziehen.'))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <div className="mb-3 rounded-md border border-sky-500/40 bg-sky-500/5 px-3 py-2 text-sm">
      <p className="font-medium">Teil- und Anzahlungsrechnungen dieses Lieferanten</p>
      <p className="text-muted-foreground">Was auf der Schlussrechnung abgezogen ist, hier übernehmen — je Rechnung entsteht eine Abzugszeile. Jede Rechnung lässt sich nur einmal abziehen.</p>
      <ul className="mt-1 space-y-0.5">
        {kandidaten.map((k) => (
          <li key={k.id}>
            <label className="flex flex-wrap items-center gap-x-3">
              <input
                type="checkbox"
                checked={wahl.has(k.id)}
                onChange={(e) => {
                  const neu = new Set(wahl)
                  if (e.target.checked) neu.add(k.id)
                  else neu.delete(k.id)
                  setWahl(neu)
                }}
              />
              <span>{ART_TEXT[k.kind]} {k.supplierInvoiceNumber}</span>
              <span className="text-muted-foreground">{k.number} · {tagText(k.invoiceDate)}</span>
              <span className="tabular-nums">netto {geldText(k.netTotal)}</span>
            </label>
          </li>
        ))}
      </ul>
      <button type="button" className={`${haupt} mt-2`} disabled={laeuft || wahl.size === 0} onClick={() => void abziehen()}>
        Abziehen ({wahl.size})
      </button>
    </div>
  )
}
